<template>
    <div class="module-time-tracker">
        <loading-indicator v-if="loading" text="Überprüfung auf Kollisionen"/>
        <template v-if="representationWarning.colliding_absences && representationWarning.colliding_absences.length">
            <div class="info">
                <i class="material-icons md-light" aria-hidden="true" role="img">info</i>
                <div>
                    An folgenden Tagen liegen Kollisionen mit den Vertretungsreglungen aufgrund abwesender Mitarbeiter vor:
                    <ul>
                        <li v-for="absence of representationWarning.colliding_absences">{{absence}}</li>
                    </ul>
                </div>
            </div>
        </template>
        <template v-if="representationWarning.invalid_regulations && representationWarning.invalid_regulations.length">
            <div class="info">
                <i class="material-icons md-light" aria-hidden="true" role="img">info</i>
                <div>
                    Die Anzahl benötigter Mitarbeiter gleicht oder übersteigt die der Mitarbeiter in der Vertretungsregelung! Bitte überprüfen Sie die folgenden Vertretungsregelungen:
                    <ul>
                        <li v-for="regulation of representationWarning.invalid_regulations">{{regulation.title}}</li>
                    </ul>
                </div>
            </div>
        </template>
        <template v-if="representationWarning.colliding_regulations && representationWarning.colliding_regulations.length">
            <template v-for="regulation of representationWarning.colliding_regulations">
                <h2>{{regulation.title}}</h2>
                <absence-calendar :year="currentYear" :regulation-id="regulation.id" :absence-id="entry.id" :display-navigation="false" tag="div"/>
            </template>
        </template>
        <template v-if="representationWarning.colliding_own_absences && representationWarning.colliding_own_absences.length">
            <div class="info">
                <i class="material-icons md-light" aria-hidden="true" role="img">info</i>
                <div>
                    An folgenden Tagen liegen Kollisionen mit eigenen Abwesenheiten vor:
                    <ul>
                        <li v-for="absence of representationWarning.colliding_own_absences">{{absence}}</li>
                    </ul>
                </div>
            </div>
        </template>

        <form-row label="Mitarbeiter" class="margin-top-10">
            <div class="time-tracker-reason-box-bold">{{entry.employee}}</div>
        </form-row>
        <form-row v-if="entry.request_reason" label="Begründung">
            <div class="time-tracker-reason-box-bold">{{entry.request_reason}}</div>
        </form-row>
        <form-row label="Grund">
            <div class="time-tracker-reason-box-bold">{{entry.reason_label}}</div>
        </form-row>
        <form-row label="Von">
            <div class="time-tracker-reason-box-bold">{{startDateTime}}</div>
        </form-row>
        <form-row label="Bis">
            <div class="time-tracker-reason-box-bold">{{endDateTime}}</div>
        </form-row>

        <template v-if="!viewOnly">
            <form-row v-if="accept" label="Arbeitszeit abziehen"
                help="Wenn Sie diese Option einschalten, wird die Zeit, in der dieser Mitarbeiter nicht arbeitet, von seiner normalen Wochenarbeitszeit abgezogen. Die Soll-Stunden des Mitarbeiters verringern sich also. Anwendungsbeispiele sind Urlaub oder Elternzeit.
                In manchen Fällen bleibt die Wochenarbeitszeit gleich, auch wenn Mitarbeiter nicht anwesend sind. Anwendungsbeispiele für diesen Fall sind ein Sabbatical oder eine dienstliche Weiterbildung. In diesen Fällen deaktivieren Sie die Option, damit die Soll-Stunden des Mitarbeiters gleich bleiben.
                Alle Abwesenheiten von Mitarbeitern werden auch zur Information von Kollegen in der Telefonleiste angezeigt."
                label-for="work_time_substract" error-name="work_time_substract" class="form-row-checkbox">
                <form-input-checkbox v-model="workTimeSubstract" name="work_time_substract" :value-on="1" :value-off="0"/>
            </form-row>
            <form-row v-else label="Grund" required
                :help="reasonToolTip"
                label-for="reason">
                <form-textarea rows="3" class="long" v-model="reason" name="reason"/>
            </form-row>
        </template>
    </div>
</template>

<script lang="ts">
    import FormInputCheckbox from '@/components/form/FormInputCheckbox.vue';
    import FormRow from '@/components/form/FormRow.vue';
    import FormTextarea from '@/components/form/FormTextarea.vue';
    import LoadingIndicator from '@/components/LoadingIndicator.vue';
    import Loader from '@/library/intranet/loader';
    import {
        ABSENCE_STATE_OPEN_UNDO_REQUEST,
    } from '@/library/modules/absences/absences';
    import {Popup, popupDefaultCancelButton} from '@/library/popup';
    import TopMessageHandler from '@/library/topMessageHandler';
    import {errorHandler, XHR_STATUS_DEFAULT, XHR_STATUS_INVALID} from '@/utils/portuna';
    import AbsenceCalendar from '@/views/absences/AbsenceCalendar.vue';
    import {Component, Prop, Vue} from 'vue-facing-decorator';
    import FormInputDate from '../../components/form/FormInputDate.vue';
    import dateHelper from '../../utils/portuna/dateHelper';

    @Component({
        name: 'AbsenceRequestChangeStatusPopup',
        methods: {dateHelper},
        components: {
            LoadingIndicator,
            AbsenceCalendar,
            FormTextarea,
            FormInputCheckbox,
            FormRow,
            FormInputDate,
        },
        emits: ['setButtons', 'reload'],
    })
    export default class AbsenceRequestChangeStatusPopup extends Vue
    {
        @Prop({
            type: Object,
            required: true,
        }) entry: IAbsencesListEntry;

        @Prop({
            type: Boolean,
            required: false,
            default: false,
        }) accept: boolean;

        @Prop({
            type: Number,
            required: true,
        }) newState: number;

        @Prop({
            type: Boolean,
            required: false,
            default: false,
        }) viewOnly: boolean;

        workTimeSubstract: boolean = false;

        representationWarning: VacationRegulationWarning = {};

        currentYear = dateHelper('Y') as number;

        reason: string = '';

        loading = false;

        get button()
        {
            return this.accept ? {
                type: 'success',
                title: 'Annehmen',
                icon: 'check_circle',
            } : this.newState === ABSENCE_STATE_OPEN_UNDO_REQUEST ? {
                type: 'primary',
                title: 'Beantragen',
                icon: 'check_circle',
            } : {
                type: 'danger',
                title: 'Ablehnen',
                icon: 'remove_circle',
            };
        }

        get reasonToolTip()
        {
            return this.newState === ABSENCE_STATE_OPEN_UNDO_REQUEST
                ? 'Hinterlegen sie den Grund für die Zurückziehung.'
                : 'Hier können Sie einen Grund für die Ablehnung hinterlegen, der dem Mitarbeiter in der E-Mail angezeigt wird.';
        }

        get startDateTime()
        {
            if (this.entry.start_time && this.entry.start_time !== '00:00:00')
            {
                return dateHelper('d.m.Y ', this.entry.start) + this.entry.start_time;
            }

            const dateTime = <string>dateHelper('d.m.Y H:i', this.entry.start ?? this.entry.startTime);

            if (dateTime.endsWith('00:00'))
            {
                return dateTime.split(' ')[0];
            }

            return dateTime;
        }

        get endDateTime()
        {
            if (this.entry.end_time && this.entry.end_time !== '00:00:00')
            {
                return dateHelper('d.m.Y ', this.entry.end) + this.entry.end_time;
            }

            const dateTime = <string>dateHelper('d.m.Y H:i', this.entry.end ?? this.entry.endTime);

            if (dateTime.endsWith('00:00'))
            {
                return dateTime.split(' ')[0];
            }

            return dateTime;
        }

        created()
        {
            this.loading = true;
            this.axios.get('/employee/absences/fetch-absence-collisions/id/' + this.entry.id + '/employee/' + this.entry.employee_id).then(({data: {data}}) =>
            {
                this.representationWarning = data;

                const buttons = [
                    popupDefaultCancelButton,
                ];

                if (!this.viewOnly)
                {
                    buttons.push({
                        ...this.button,
                        cmd: (popup: Popup) =>
                        {
                            if ((!this.accept || this.newState === ABSENCE_STATE_OPEN_UNDO_REQUEST) && this.reason.trim().length === 0)
                            {
                                return TopMessageHandler.error('Sie müssen ein Grund angeben.');
                            }

                            Loader.show('Arbeitszeiten werden berechnet.');

                            this.axios.post(
                                '/employee/absences/request-change-status/id/' + this.entry.id + '/new-state/' + String(this.newState),
                                {
                                    reason: this.reason,
                                    work_time_substract: this.workTimeSubstract,
                                },
                            )
                            .then((response) =>
                            {
                                const {
                                          status,
                                          data,
                                      } = response.data;

                                if (status === XHR_STATUS_INVALID)
                                {
                                    this.representationWarning = data;

                                    throw response;
                                }

                                this.representationWarning = {};

                                if (status !== XHR_STATUS_DEFAULT)
                                {
                                    throw response;
                                }

                                this.emitter.emit('reload');
                                this.$emit('reload');
                                popup.close();

                                TopMessageHandler.success(data.message);
                            })
                            .catch(errorHandler)
                            .finally(Loader.hide);
                        },
                    } as PopupButton);
                }

                this.emitter.emit('setButtons', buttons);
                this.$emit('setButtons', buttons);
            }).catch(error =>
            {
                errorHandler(error);
                this.emitter.emit('close');
                this.$emit('close');
            }).finally(() =>
            {
                this.loading = false;
            });
        }
    }
</script>